import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronDown, Loader2, Shield } from "lucide-react";
import checkPermission from "../../utils/permission";
import { ApiService } from "../../services/api";

interface PermissionItem {
  id: string;
  key: string;
  name: string;
  assigned: boolean;
}

export default function DepartmentPermissionsPage() {
  const navigate = useNavigate();
  const { departmentId } = useParams<{ departmentId: string }>();
  const hasEditPermission = checkPermission("department_update");

  useEffect(() => {
    if (!hasEditPermission) {
      navigate("/employees");
    }
  }, [hasEditPermission, navigate]);

  const [permissions, setPermissions] = useState<PermissionItem[]>([]);
  const [departmentName, setDepartmentName] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [applyToChildren, setApplyToChildren] = useState<boolean>(false);
  const assignedPermissionCount = permissions.filter((permission) => permission.assigned).length;
  const sectionClassName = "rounded-xl border border-slate-200 bg-white p-4 sm:p-5";
  const sectionBadgeClassName =
    "inline-flex items-center rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700";

  const ensureLessonSectionPermissions = (list: PermissionItem[]): PermissionItem[] => {
    const required = [
      { key: "lesson_section_add", name: "Ders Bölümü Ekle" },
      { key: "lesson_section_update", name: "Ders Bölümü Güncelle" },
      { key: "lesson_section_delete", name: "Ders Bölümü Sil" },
    ];
    const byKey = new Map(list.map((p) => [p.key, p] as const));
    for (const r of required) {
      if (!byKey.has(r.key)) {
        byKey.set(r.key, {
          id: r.key,
          key: r.key,
          name: r.name,
          assigned: false,
        });
      }
    }
    return Array.from(byKey.values());
  };

  useEffect(() => {
    const load = async () => {
      if (!departmentId) return;
      try {
        setLoading(true);
        setError("");

        const [permissionsData, departmentData] = await Promise.all([
          ApiService.getDepartmentPermissions(departmentId),
          ApiService.getDepartment(departmentId),
        ]);

        setPermissions(ensureLessonSectionPermissions(permissionsData));
        setDepartmentName(departmentData.name || "");
      } catch (err: any) {
        setError(err.message || "Veriler yüklenemedi");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [departmentId]);

  const togglePermission = (key: string) => {
    setPermissions((prev) => prev.map((p) => (p.key === key ? { ...p, assigned: !p.assigned } : p)));
  };

  const handleSave = async () => {
    if (!departmentId) return;
    try {
      setSaving(true);
      setError("");
      const assignedKeys = permissions.filter((p) => p.assigned).map((p) => p.key);

      await ApiService.updateDepartmentPermissions(departmentId, assignedKeys, applyToChildren ? { childDepartment: true } : undefined);

      navigate("/employees?tab=departments");
    } catch (err: any) {
      setError(err.message || "İzinler kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  if (!hasEditPermission) {
    return null;
  }

  return (
    <div className="h-[88vh] bg-gray-100 overflow-hidden flex flex-col">
      <nav className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
        <div className="px-6 py-3">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">Üyeler</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-600">
              <button onClick={() => navigate("/employees?tab=users")} className={`pb-3 transition-all duration-200 text-gray-500 hover:text-gray-900`}>
                Kullanıcılar
              </button>
              <button
                onClick={() => navigate("/employees?tab=departments")}
                className={`pb-3 transition-all duration-200 text-gray-900 font-semibold border-b-2 border-gray-900`}>
                Departmanlar
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex-1 overflow-y-auto p-5 sm:p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
            <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Departman İzinleri</h1>
                <p className="text-sm text-gray-600 mt-1">
                  {departmentName ? `${departmentName} için izinleri yönetin.` : "Departmanın sahip olacağı izinleri yönetin."}
                </p>
                <div className="mt-3 h-1 w-20 rounded-full bg-blue-200" />
              </div>
              {!loading && permissions.length > 0 && (
                <div className="flex items-center gap-2 self-start">
                  <span className="rounded-full border border-green-200 bg-green-50 px-3 py-1.5 text-sm font-semibold text-green-700">
                    Açık: {assignedPermissionCount}
                  </span>
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-semibold text-slate-700">
                    Toplam: {permissions.length}
                  </span>
                </div>
              )}
            </div>

            {error && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-lg">{error}</div>}

            <div className={`${sectionClassName} mb-5`}>
              <div className="mb-3">
                <span className={`${sectionBadgeClassName} gap-1.5`}>
                  <Shield className="w-3.5 h-3.5" />
                  Izin Yonetimi
                </span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h3 className="text-gray-900 font-medium">Departman izinlerini secin</h3>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-700">Alt Departmanlara Uygula</span>
                  <button
                    onClick={() => setApplyToChildren(!applyToChildren)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${applyToChildren ? "bg-green-600" : "bg-red-600"
                      }`}
                    aria-pressed={applyToChildren}>
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${applyToChildren ? "translate-x-6" : "translate-x-1"
                        }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : permissions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Shield className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-sm">Henüz izin tanımlanmamış</p>
              </div>
            ) : (
              <PermissionsGrouped permissions={permissions} onToggle={togglePermission} />
            )}

            <div className="mt-6 border-t border-slate-200 pt-3">
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => navigate(-1)}
                  disabled={saving}
                  className="px-4 py-2 bg-white text-gray-700 rounded-md border border-gray-300 hover:bg-gray-50 text-sm disabled:opacity-50">
                  İptal
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || loading}
                  className="flex items-center justify-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60">
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Kaydediliyor...
                    </>
                  ) : (
                    "İzinleri Kaydet"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PermissionsGrouped({ permissions, onToggle }: { permissions: PermissionItem[]; onToggle: (key: string) => void }) {
  type ModuleCategory = {
    key: string;
    label: string;
    prefixes: string[];
    cleanPrefixes?: string[];
    prefixLabels?: Record<string, string>;
  };

  const moduleCategories: ModuleCategory[] = [
    {
      key: "users",
      label: "Üyeler",
      prefixes: ["user", "department"],
      cleanPrefixes: ["Kullanıcı", "Departman"],
      prefixLabels: {
        user: "Kullanıcı",
        department: "Departman",
      },
    },
    {
      key: "announcements",
      label: "Duyurular",
      prefixes: ["announcement"],
      cleanPrefixes: ["Duyuru"],
    },
    {
      key: "surveys",
      label: "Anketler",
      prefixes: ["survey"],
      cleanPrefixes: ["Anket"],
    },
    {
      key: "meetings",
      label: "Toplantılar",
      prefixes: ["meet_attendance", "meet_summary", "meet"],
      cleanPrefixes: ["Toplantı"],
      prefixLabels: {
        meet: "Toplantı",
        meet_attendance: "Katılım",
        meet_summary: "Özet",
      },
    },
    {
      key: "calendar",
      label: "Takvim",
      prefixes: ["calendar"],
      cleanPrefixes: ["Takvim"],
    },
    {
      key: "lessons",
      label: "E-Eğitim",
      prefixes: ["lesson_section", "lesson_calendar", "lesson_qa", "lesson_vote", "lesson_comment", "lesson_note", "lesson"],
      cleanPrefixes: ["Ders Bölümü", "Ders Takvimi", "Ders Soruları", "Ders Oyları", "Ders Yorumları", "Ders Notları", "Ders"],
      prefixLabels: {
        lesson: "Ders",
        lesson_section: "Ders Bölümü",
        lesson_calendar: "Ders Takvimi",
        lesson_qa: "Ders Soruları",
        lesson_vote: "Ders Oyları",
        lesson_comment: "Ders Yorumları",
        lesson_note: "Ders Notları",
      },
    },
    {
      key: "speed_reading",
      label: "Hızlı Okuma",
      prefixes: ["speed_reading"],
      cleanPrefixes: ["Hızlı Okuma"],
    },
    {
      key: "planning",
      label: "Planlama",
      prefixes: [
        "plan_class_lesson_assignment",
        "plan_teacher_availability",
        "plan_teacher_assignment",
        "plan_timetable",
        "plan_settings",
        "plan_schedule",
        "plan_lesson",
        "plan_teacher",
        "plan_class",
      ],
      cleanPrefixes: ["Planlama"],
      prefixLabels: {
        plan_lesson: "Ders",
        plan_teacher: "Öğretmen",
        plan_class: "Sınıf",
        plan_schedule: "Ders Saati",
        plan_timetable: "Zaman Çizelgesi",
        plan_settings: "Ayarlar",
        plan_teacher_availability: "Öğretmen Müsaitlik",
        plan_teacher_assignment: "Öğretmen Ders Ataması",
        plan_class_lesson_assignment: "Sınıf Ders Ataması",
      },
    },
    {
      key: "appointments",
      label: "Randevular",
      prefixes: ["appointment"],
      cleanPrefixes: ["Randevu"],
    },
    {
      key: "tasks",
      label: "Görevler",
      prefixes: ["task"],
      cleanPrefixes: ["Görev"],
    },
    {
      key: "goals",
      label: "Hedefler",
      prefixes: ["goal"],
      cleanPrefixes: ["Hedef"],
    },
    {
      key: "homework",
      label: "Ödevler",
      prefixes: ["homework"],
      cleanPrefixes: ["Ödev"],
    },
    {
      key: "practice_exam",
      label: "Denemeler",
      prefixes: ["practice_exam"],
      cleanPrefixes: ["Yeni Deneme", "Deneme", "Öğrenci Denemelerini"],
    },
    {
      key: "reports",
      label: "Raporlar",
      prefixes: ["homework_report", "test_report", "student_report", "parent_report", "department_homework_report", "appointment_report"],
      cleanPrefixes: ["Ödev Raporu", "Test Raporu", "Öğrenci Raporu", "Veli Raporu", "Şube Ödev Raporu", "Randevu Raporu"],
      prefixLabels: {
        homework_report: "Ödev Raporu",
        test_report: "Test Raporu",
        student_report: "Öğrenci Raporu",
        parent_report: "Veli Raporu",
        department_homework_report: "Şube Ödev Raporu",
        appointment_report: "Randevu Raporu",
      },
    },
    {
      key: "files",
      label: "Dosyalar",
      prefixes: ["file"],
      cleanPrefixes: ["Dosya"],
    },
    {
      key: "maarif_test",
      label: "Test Oluşturma",
      prefixes: ["maarif_test"],
      cleanPrefixes: ["Oluşturulan Testleri", "Yeni Test", "Test"],
    },
    {
      key: "maarif_question",
      label: "Soru Sihirbazı",
      prefixes: ["maarif_question"],
      cleanPrefixes: ["Soru Sihirbazı", "Yeni Soru", "Soruyu"],
    },
    {
      key: "maarif_batch",
      label: "Toplu Soru Üretimi",
      prefixes: ["maarif_batch"],
      cleanPrefixes: ["Maarif Toplu Üretim İşlerini", "Maarif Toplu Üretim İşi"],
    },
    {
      key: "question_bank",
      label: "Dizgici",
      prefixes: ["question_bank"],
      cleanPrefixes: ["Dizgiciyi", "Yeni Dizgi Dosyası", "Dizgi Dosyası"],
    },
    {
      key: "social",
      label: "Sosyal Medya",
      prefixes: ["social"],
      cleanPrefixes: ["Sosyal Medya"],
    },
    {
      key: "whiteboard",
      label: "Beyaz Tahta",
      prefixes: ["whiteboard"],
      cleanPrefixes: ["Beyaz Tahta"],
    },
    {
      key: "notification",
      label: "Bildirimler",
      prefixes: ["notification"],
      cleanPrefixes: ["Bildirim"],
    },
    {
      key: "todos",
      label: "Yapılacaklar",
      prefixes: ["todo_daily_routine", "todo_category", "todo_tag", "todo_item", "todo"],
      cleanPrefixes: ["Yapılacaklar"],
      prefixLabels: {
        todo: "Yapılacaklar",
        todo_category: "Kategori",
        todo_tag: "Etiket",
        todo_item: "Görev",
        todo_daily_routine: "Günlük Rutin",
      },
    },
    {
      key: "time_tracking",
      label: "Zaman Merkezi",
      prefixes: ["time_tracking"],
      cleanPrefixes: ["Zaman Merkezi"],
    },
    {
      key: "navigator",
      label: "Koçum",
      prefixes: ["navigator"],
      cleanPrefixes: ["Koçum"],
    },
    {
      key: "payment",
      label: "Ödemeler",
      prefixes: ["payment"],
      cleanPrefixes: ["Ödeme"],
    },
    {
      key: "setting",
      label: "Sistem Ayarları",
      prefixes: ["setting"],
      cleanPrefixes: ["Ayarları", "Ayarlar"],
    },
    {
      key: "other",
      label: "Diğer",
      prefixes: [],
      cleanPrefixes: [],
    },
  ];
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({});

  const getPermissionMatch = (permissionKey: string) => {
    let bestMatch = moduleCategories[moduleCategories.length - 1];
    let bestPrefix = "other";
    let bestLength = -1;

    for (const category of moduleCategories) {
      for (const prefix of category.prefixes) {
        const matches = permissionKey === prefix || permissionKey.startsWith(`${prefix}_`);
        if (matches && prefix.length > bestLength) {
          bestMatch = category;
          bestPrefix = prefix;
          bestLength = prefix.length;
        }
      }
    }

    return { category: bestMatch, prefix: bestPrefix };
  };

  const grouped = permissions.reduce((acc, permission) => {
    const { category, prefix } = getPermissionMatch(permission.key);
    acc[category.key] ||= {};
    (acc[category.key][prefix] ||= []).push(permission);
    return acc;
  }, {} as Record<string, Record<string, PermissionItem[]>>);

  const getCleanPermissionName = (permissionName: string, cleanPrefixes: string[] = []): string => {
    const cleanName = permissionName.trim();

    for (const prefix of cleanPrefixes) {
      if (cleanName.startsWith(`${prefix} `)) {
        return cleanName.substring(prefix.length + 1);
      }
    }

    return cleanName;
  };

  const toggleCategory = (categoryKey: string) => {
    setOpenCategories((prev) => ({
      ...prev,
      [categoryKey]: !prev[categoryKey],
    }));
  };

  const getSubgroupLabel = (category: ModuleCategory, prefix: string) => {
    return category.prefixLabels?.[prefix] || category.label;
  };

  return (
    <div className="space-y-5">
      {moduleCategories.map((category) => {
        const categoryGroups = category.prefixes
          .filter((prefix) => grouped[category.key]?.[prefix]?.length > 0)
          .map((prefix) => ({
            prefix,
            label: getSubgroupLabel(category, prefix),
            items: grouped[category.key][prefix].slice().sort((a, b) => a.name.localeCompare(b.name, "tr")),
          }));
        if (grouped[category.key]?.other?.length) {
          categoryGroups.push({
            prefix: "other",
            label: "Diğer",
            items: grouped[category.key].other.slice().sort((a, b) => a.name.localeCompare(b.name, "tr")),
          });
        }
        const items = categoryGroups.flatMap((group) => group.items);
        if (items.length === 0) return null;

        const isOpen = openCategories[category.key] ?? false;
        const assignedCount = items.filter((permission) => permission.assigned).length;

        return (
          <div key={category.key} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <button
              type="button"
              onClick={() => toggleCategory(category.key)}
              className="flex w-full items-center justify-between gap-4 px-4 py-4 sm:px-5 text-left"
              aria-expanded={isOpen}>
              <div className="min-w-0">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">{category.label}</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {assignedCount} açık / {items.length} toplam
                </p>
              </div>
              <ChevronDown
                className={`h-5 w-5 flex-shrink-0 text-gray-500 transition-transform duration-300 ${isOpen ? "rotate-180" : "rotate-0"}`}
              />
            </button>

            <div className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
              <div className="overflow-hidden">
                <div className="px-4 pb-4 sm:px-5 sm:pb-5">
                  <div className="grid max-h-72 grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-3 overflow-auto pr-1">
                    {categoryGroups.map((group) => (
                      <div key={group.prefix} className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
                        <h4 className="mb-2 text-sm font-semibold text-gray-800">{group.label}</h4>
                        <div className="space-y-2">
                          {group.items.map((permission) => {
                            const displayName = getCleanPermissionName(permission.name, [group.label, ...(category.cleanPrefixes || [])]);

                            return (
                              <div key={permission.key} className="flex items-start justify-between gap-3 rounded-lg border border-slate-100 bg-white px-3 py-2.5">
                                <span className="text-sm leading-5 text-gray-700 break-words flex-1 min-w-0" title={permission.name}>
                                  {displayName}
                                </span>
                                <button
                                  onClick={() => onToggle(permission.key)}
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${permission.assigned ? "bg-green-600" : "bg-red-600"}`}
                                  aria-pressed={permission.assigned}>
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${permission.assigned ? "translate-x-6" : "translate-x-1"}`}
                                  />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
